import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTheme } from 'styled-components'
import { formatPercentage, useSwitch } from '@chilecompra/react-kit'
import { DataTable, Select, Switch, useSelect } from '@chilecompra/react-kit/components'
import { CURRENCY_VALUES } from '../../config/settings/constants'
import exportToImage, { exportToCsv } from '../../modules/utils/exports'
import { formatAmount } from '../../modules/utils/formatters'
import generateYearsBetween from '../../modules/utils/years'
import { onGetProviderSalesDistributionThunk } from './ProviderSalesDistributionSection.actions'
import BubblesChart from '../../components/Charts/BubblesChart'
import RegionalPurchasingMechanisms from '../../components/Infographics/RegionalPurchasingMechanisms'
import DataLayout, { DataLayoutChild, DataLayoutViews, useDataLayout } from '../../components/Layouts/DataLayout'
import {
  Card,
  CardIndicator,
  Description,
  GridContainer,
  GridItem,
  Info,
  Italic,
  Root,
  Strong,
  Title
} from './ProviderSalesDistributionSection.styles'

/**
 * The ProviderSalesDistributionSection's container.
 */
const ProviderSalesDistributionSection = () => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const {
    endYear,
    latestUpdate,
    modalities: mechanismOptions,
    sectors: sectorOptions,
    startYear
  } = useSelector(state => state.topNavbar)
  const { dni } = useSelector(state => state.providerSection)
  const { csv, data, lengthData, loading, totalAmount, totalAmountUSD, totalOrders } = useSelector(
    state => state.providerSalesDistributionSection
  )
  const { value: yearValue, onChange: handleChangeYear } = useSelect({
    initialValue: endYear,
    changeCallback: updatedValue =>
      dispatch(
        onGetProviderSalesDistributionThunk({
          dni,
          mechanismId: mechanismValue,
          sectorId: sectorValue,
          year: updatedValue
        })
      ),
    overwriteCallback: () => endYear
  })
  const { value: mechanismValue, onChange: handleChangeMechanism } = useSelect({
    initialValue: 7,
    changeCallback: updatedValue =>
      dispatch(
        onGetProviderSalesDistributionThunk({
          dni,
          mechanismId: updatedValue,
          sectorId: sectorValue,
          year: yearValue
        })
      )
  })
  const { value: sectorValue, onChange: handleChangeSector } = useSelect({
    initialValue: 8,
    changeCallback: updatedValue =>
      dispatch(
        onGetProviderSalesDistributionThunk({
          dni,
          mechanismId: mechanismValue,
          sectorId: updatedValue,
          year: yearValue
        })
      )
  })
  const { value: currencyValue, onChange: handleChangeCurrency } = useSwitch({
    changeCallback: () =>
      dispatch(
        onGetProviderSalesDistributionThunk({
          dni,
          mechanismId: mechanismValue,
          sectorId: sectorValue,
          year: yearValue
        })
      )
  })
  const {
    disableCaptureImage,
    disableDownloadFile,
    ref: dataLayoutRef,
    onCaptureImage: handleCaptureImage,
    onChangeView: handleChangeView,
    onDownloadFile: handleDownloadFile
  } = useDataLayout({
    dependencies: [csv, yearValue],
    captureImageCallback: () =>
      exportToImage('sector-purchasing-mechanisms-horizontal-bar-chart', `Info_Sector_Mecanismos_Compra_${yearValue}`),
    downloadFileCallback: () => exportToCsv(csv, `Info_Sector_Mecanismos_Compra_${yearValue}`)
  })
  const handleTooltipFormatter = useCallback(function () {
    return `
      <ul>
        <li>
          - <strong>${
            CURRENCY_VALUES.CLP
          }${formatAmount(this.y)} (${CURRENCY_VALUES.USD} ${formatAmount(this.point?.amountUSD)})</strong> transados en <strong>${this.x}</strong>.
        </li>
        <br />
        <li>
          - Este corresponde al <strong>${formatPercentage(
            this.point?.percentage
          )}</strong> de su monto total transado durante el año.
        </li>
        <br />
        <li>
          - <strong>${this.point.orders}</strong> órdenes de compra.
        </li>
      </ul>
    `
  }, [])
  const yearsOptions = useMemo(() => generateYearsBetween(endYear, startYear), [endYear, startYear])
  const currentMechanism = useMemo(
    () => mechanismOptions.find(mechanismOption => mechanismOption.value === mechanismValue),
    [mechanismValue]
  )
  const currentSector = useMemo(
    () => sectorOptions.find(sectorOption => sectorOption.value === sectorValue),
    [sectorValue]
  )
  const getEnhancedData = useCallback(
    (currency, collection = []) =>
      collection.map(datum => {
        if (!datum.amount) {
          return datum
        }

        return {
          ...datum,
          y: currency ? datum.amountUSD : datum.amount
        }
      }),
    []
  )

  useEffect(() => {
    if (!dni || !endYear) {
      return
    }

    dispatch(onGetProviderSalesDistributionThunk({ dni, mechanismId: 7, sectorId: 8, year: endYear }))
  }, [dni, endYear])

  return (
    <Root>
      <GridContainer padding="0 0 23px">
        <GridItem xs={12}>
          <Title>Distribución de sus ventas</Title>
        </GridItem>
      </GridContainer>
      <GridContainer padding="0 0 28px">
        <GridItem xs={12}>
          <Description>
            Conoce las cifras y distribución de las ventas realizadas por el proveedor según el año, mecanismo de compra
            y sector del Estado seleccionado.
          </Description>
        </GridItem>
      </GridContainer>
      <GridContainer>
        <GridItem xs={12}>
          <DataLayout
            disableCaptureImage={disableCaptureImage}
            disableDownloadFile={disableDownloadFile}
            header={
              <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                <GridItem md={6} xs={12}>
                  <GridContainer alignItems="center" justifyContent="space-between" spacing={1}>
                    <GridItem md={4} xs={12}>
                      <Select
                        label="AÑO"
                        loading={loading}
                        options={yearsOptions}
                        value={yearValue}
                        onChange={handleChangeYear}
                      />
                    </GridItem>
                    <GridItem md={4} xs={12}>
                      <Select
                        label="MECANISMO DE COMPRA"
                        loading={loading}
                        options={mechanismOptions}
                        value={mechanismValue}
                        onChange={handleChangeMechanism}
                      />
                    </GridItem>
                    <GridItem md={4} xs={12}>
                      <Select
                        label="SECTOR DEL ESTADO"
                        loading={loading}
                        options={sectorOptions}
                        value={sectorValue}
                        onChange={handleChangeSector}
                      />
                    </GridItem>
                  </GridContainer>
                </GridItem>
                <GridItem md={2} xs={12}>
                  <Switch
                    checked={currencyValue}
                    color="primary"
                    labelLeft="CLP"
                    labelRight="USD"
                    onChange={handleChangeCurrency}
                  />
                </GridItem>
              </GridContainer>
            }
            infographic={<RegionalPurchasingMechanisms />}
            noData={lengthData === 0}
            ref={dataLayoutRef}
            updatedDate={latestUpdate}
            onCaptureImage={handleCaptureImage}
            onChangeView={handleChangeView}
            onDownloadFile={handleDownloadFile}
          >
            <DataLayoutChild view={DataLayoutViews.CHART}>
              <GridContainer>
                <GridItem xs={12}>
                  <BubblesChart
                    categories={[
                      'Enero',
                      'Febrero',
                      'Marzo',
                      'Abril',
                      'Mayo',
                      'Junio',
                      'Julio',
                      'Agosto',
                      'Septiembre',
                      'Octubre',
                      'Noviembre',
                      'Diciembre'
                    ]}
                    color={theme.palette.brand.primary}
                    data={getEnhancedData(currencyValue, data)}
                    id="provider-sales-distribution-bubbles-chart"
                    tooltipFormatter={handleTooltipFormatter}
                  />
                </GridItem>
              </GridContainer>
              <GridContainer padding="44px 0 0" spacing={8}>
                <GridItem xs={1} />
                <GridItem xs={5}>
                  <Card enableBorder color="blank">
                    <CardIndicator>{`$${formatAmount(currencyValue ? totalAmountUSD : totalAmount)}`}</CardIndicator>
                    Monto transado a través de <Strong>{currentMechanism.name}</Strong> en el sector{' '}
                    <Strong>{currentSector.name}</Strong> durante {yearValue}.
                  </Card>
                </GridItem>
                <GridItem xs={5}>
                  <Card enableBorder color="blank">
                    <CardIndicator>{totalOrders}</CardIndicator>
                    Órdenes de compra asociadas a <Strong>{currentMechanism.name}</Strong> en el sector{' '}
                    <Strong>{currentSector.name}</Strong> durante {yearValue}.
                  </Card>
                </GridItem>
                <GridItem xs={1} />
              </GridContainer>
              <GridContainer margin="19px 0 0">
                <GridItem xs={12}>
                  <Info>{!currencyValue ? 'Millones de pesos (CLP)' : ' Miles de dólares (USD)'}</Info>
                </GridItem>
              </GridContainer>
              <GridContainer margin="19px 0 0">
                <GridItem xs={12}>
                  <Info>
                    <Italic>
                      La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                      impuestos)
                    </Italic>
                  </Info>
                </GridItem>
              </GridContainer>
            </DataLayoutChild>
            <DataLayoutChild view={DataLayoutViews.TABLE}>
              <GridContainer padding="0 0 24px">
                <GridItem xs={12}>
                  <DataTable
                    headers={[
                      { label: 'Mes', value: 1 },
                      { label: 'Proveedor', value: 2 },
                      { label: 'Sector', value: 3 },
                      { label: 'Nº de órdenes de compra', value: 4 },
                      { label: 'Monto total', value: 5 },
                      { label: 'Monto total ($USD)', value: 6 }
                    ]}
                    headersCellsProps={[
                      { enableSort: true, width: '10%' },
                      { enableSort: true, width: '30%' },
                      { enableSort: true, width: '20%' },
                      { enableSort: true, width: '20%' },
                      { enableSort: true, width: '15%' },
                      { enableSort: true, width: '15%' }
                    ]}
                    initialRows={6}
                    loading={loading}
                    records={csv.slice(1)}
                    recordsDecorators={[
                      ([, month]) => month,
                      ([, , provider]) => provider,
                      ([, , , sector]) => sector,
                      ([, , , , orders]) => formatAmount(orders),
                      ([, , , , , amount]) => `$${formatAmount(amount)}`,
                      ([, , , , , , amountUSD]) => `$${formatAmount(amountUSD)}`
                    ]}
                  />
                </GridItem>
              </GridContainer>
              <GridContainer margin="30px 0 0">
                <GridItem xs={12}>
                  <Info>
                    <Italic>
                      La información de montos transados dentro de esta sección corresponde al monto bruto (monto neto +
                      impuestos)
                    </Italic>
                  </Info>
                </GridItem>
              </GridContainer>
            </DataLayoutChild>
          </DataLayout>
        </GridItem>
      </GridContainer>
    </Root>
  )
}

export default ProviderSalesDistributionSection
